import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getTimeUntil, type TimeUntilTranslations } from "../../devices";
import {
  getTimeAgo,
  type TimeTranslations,
} from "../../../../../../utils/getDate";
import type { Trailer, ThemeClasses } from "../types";
import { getStatusColor, getStatusLabel, getTypeFormatter } from "../utils";
import { useVehiclesRealtimeStore } from "../../../../../../store/admin/vehiclesRealtimeStore";
import type { TFunction } from "i18next";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";

interface TrailerGridCardProps {
  trailer: Trailer;
  themeClasses: ThemeClasses;
  onEdit: (trailer: Trailer) => void;
  onDetails: (trailer: Trailer) => void;
  onDeviceClick?: (trailer: Trailer) => void;
  onDelete?: (trailer: Trailer) => void;
  t: TFunction;
  disableCRUD: boolean;
}

export function TrailerGridCard({
  trailer,
  themeClasses,
  onEdit,
  onDetails,
  onDeviceClick,
  onDelete,
  t,
  disableCRUD,
}: TrailerGridCardProps) {
  const navigate = useNavigate();
  const { t: tLite } = useTranslation("lite");
  const setFocusedVehicleId = useVehiclesRealtimeStore(
    (state) => state.setFocusedVehicleId,
  );
  const setHighlightedVehicleId = useVehiclesRealtimeStore(
    (state) => state.setHighlightedVehicleId,
  );
  const { isMonitor, isReadOnly } = useIsMonitor();
  const plattform = import.meta.env.VITE_THEME;
  const timeTranslations: TimeTranslations = useMemo(
    () => ({
      noData: tLite("time.noData"),
      rightNow: tLite("time.rightNow"),
      min: tLite("time.min"),
      hour: tLite("time.hour"),
      hours: tLite("time.hours"),
      day: tLite("time.day"),
      days: tLite("time.days"),
      month: tLite("time.month"),
      months: tLite("time.months"),
      and: tLite("time.and"),
    }),
    [tLite],
  );

  const timeUntilTranslations: TimeUntilTranslations = useMemo(
    () => ({
      alreadyPassed: tLite("time.alreadyPassed"),
      inDays: tLite("time.inDays"),
      inDaysPlural: tLite("time.inDaysPlural"),
      inHours: tLite("time.inHours"),
      inHoursPlural: tLite("time.inHoursPlural"),
      inMinutes: tLite("time.inMinutes"),
      inMinutesPlural: tLite("time.inMinutesPlural"),
      inSeconds: tLite("time.inSeconds"),
    }),
    [tLite],
  );
  // Verificar si el remolque tiene un device conectado (directo o a través de vehicle)
  const hasDevice = !!trailer.device_id || !!trailer.device;
  // Verificar si el remolque tiene un vehículo asociado
  const hasVehicle = !!trailer.vehicle_id && trailer.vehicle_id !== "N/A";

  const handleViewOnMap = () => {
    if (!hasVehicle || !trailer.vehicle_id) return;

    setFocusedVehicleId(trailer.vehicle_id);

    setHighlightedVehicleId(trailer.vehicle_id, 5000);

    navigate("/map");
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden hover:shadow-lg transition-all`}
    >
      {/* Trailer Header */}
      <div className="p-5 pb-0">
        <div className="relative">
          <span
            className={`absolute top-2 right-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
              trailer.status,
            )}`}
          >
            {getStatusLabel(trailer.status, t)}
          </span>

          {trailer.image ? (
            <img
              src={trailer.image}
              alt={trailer.name}
              className="w-full h-48 object-cover"
            />
          ) : (
            <div className="flex h-48 items-center justify-center bg-gray-100 rounded-lg">
              <i className="ri-file-image-line text-6xl text-gray-400"></i>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between my-2">
          <div className="flex flex-col gap-2">
            <h3 className={`font-semibold ${themeClasses.text} text-lg`}>
              {getTypeFormatter(trailer.type || "", t)}
            </h3>
            <p className={`-mt-2 text-sm ${themeClasses.textSecondary}`}>
              {trailer.brand} {trailer.model} {trailer.year} • {trailer.plate}
            </p>
          </div>
          <div className="text-right flex flex-col gap-2">
            {hasDevice ? (
              <>
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  {t("trailers.fields.deviceConnected") || "Device"}
                </p>
                <p className={`-mt-2 text-sm ${themeClasses.text} font-bold`}>
                  {trailer.device?.name || trailer.device?.identifier || "GPS"}
                </p>
              </>
            ) : (
              <>
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  {t("trailers.fields.vehicleAssigned")}
                </p>
                <p className={`-mt-2 text-sm ${themeClasses.text} font-bold`}>
                  {trailer.vehicle?.name || "N/A"}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-5 pt-2">
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>
              {t("trailers.fields.capacity")}
            </p>
            <p className={`text-sm ${themeClasses.text} font-semibold`}>
              {trailer.capacity || "N/A"}
            </p>
          </div>
          <div>
            <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>
              {t("trailers.fields.maximumWeight")}
            </p>
            <p className={`text-sm ${themeClasses.text} font-semibold`}>
              {trailer.maximumWeight || "N/A"} t
            </p>
          </div>
          <div>
            <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>
              {t("trailers.fields.lastInspection")}
            </p>
            <p className={`text-sm ${themeClasses.text} font-semibold`}>
              {trailer.inspectionDate?._seconds
                ? getTimeAgo(trailer.inspectionDate._seconds, timeTranslations)
                : "N/A"}
            </p>
          </div>
          <div className="flex flex-col w-full col-span-3">
            <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>
              {t("trailers.fields.location")}
            </p>
            <p className={`text-sm ${themeClasses.text}`}>
              {trailer.lastAddress || "N/A"}
            </p>
          </div>

          <div
            className={`flex items-center w-full col-span-3 gap-2 px-4 p-2 rounded-lg ${themeClasses.cardBg} border ${themeClasses.cardBorder} `}
          >
            <i className={`ri-shield-check-line ${themeClasses.text}`}></i>
            <div className="flex w-full justify-between">
              <span className={`${themeClasses.textSecondary}`}>
                {t("trailers.fields.nextInspection")}:
              </span>
              <p className={`${themeClasses.text}`}>
                {trailer.nextInspectionDate?._seconds
                  ? getTimeUntil(
                      trailer.nextInspectionDate._seconds,
                      undefined,
                      timeUntilTranslations,
                    )
                  : "N/A"}
              </p>
            </div>
          </div>
          <div
            className={`flex items-center w-full col-span-3 gap-2 ${themeClasses.cardBg}`}
          >
            <div className={`bg-blue-200 p-2 rounded-xl`}>
              <p className={`text-xs font-bold text-blue-600`}>
                {getTypeFormatter(trailer.type || "", t)}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          {!isMonitor && !isReadOnly && (
            <>
              <CRUDTooltip className="flex-1">
                <button
                  onClick={() => onEdit(trailer)}
                  className={`flex-1 py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors`}
                  disabled={disableCRUD}
                >
                  {t("trailers.actions.edit")}
                </button>
              </CRUDTooltip>
              {/* Botón conectar/desconectar Device */}
              {plattform === "central-gps" && (
                <CRUDTooltip>
                  <button
                    onClick={() => onDeviceClick?.(trailer)}
                    disabled={disableCRUD}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors relative ${
                      hasDevice
                        ? "bg-blue-500 hover:bg-blue-600 text-white"
                        : `${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`
                    }`}
                    title={
                      hasDevice
                        ? t("trailers.device.disconnect")
                        : t("trailers.device.connect")
                    }
                  >
                    <i className="ri-gps-line"></i>
                    {!hasDevice && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full flex items-center justify-center">
                        <i className="ri-add-line text-[8px] text-gray-900"></i>
                      </span>
                    )}
                  </button>
                </CRUDTooltip>
              )}
              <button
                onClick={handleViewOnMap}
                className={`py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors max-w-10 disabled:opacity-50 disabled:cursor-not-allowed`}
                disabled={!hasDevice}
                title={
                  hasDevice
                    ? t("trailers.viewOnMap")
                    : t("trailers.noDeviceConnected")
                }
              >
                <i className="ri-map-pin-line"></i>
              </button>
            </>
          )}
          <button
            onClick={() => onDetails(trailer)}
            className="py-2 px-3 bg-primary-500 hover:bg-primary-400 rounded-lg text-sm font-medium transition-colors flex-1"
          >
            {t("trailers.actions.details")}
          </button>
          {onDelete && !isMonitor && !isReadOnly && (
            <CRUDTooltip>
              <button
                onClick={() => onDelete(trailer)}
                disabled={disableCRUD}
                className="py-2 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={t("trailers.actions.delete", "Eliminar")}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </CRUDTooltip>
          )}
        </div>
      </div>
    </div>
  );
}
